import React from 'react';
import type { WxbStatusTone } from '../StatusBadge/StatusBadge';
import './RiskMatrix.css';

type RiskScore = 1 | 2 | 3 | 4 | 5;

export interface WxbRiskMatrixItem {
  key: string;
  likelihood: RiskScore;
  impact: RiskScore;
  label: React.ReactNode;
  tone?: WxbStatusTone;
  count?: React.ReactNode;
}

export interface WxbRiskMatrixProps extends React.HTMLAttributes<HTMLDivElement> {
  items: WxbRiskMatrixItem[];
  likelihoodLabel?: React.ReactNode;
  impactLabel?: React.ReactNode;
}

const SCORE_VALUES: RiskScore[] = [5, 4, 3, 2, 1];
const IMPACT_VALUES: RiskScore[] = [1, 2, 3, 4, 5];

const joinClasses = (...classes: Array<string | false | undefined>) =>
  classes.filter(Boolean).join(' ');

const resolveTone = (item?: WxbRiskMatrixItem): WxbStatusTone => {
  if (item?.tone) return item.tone;
  if (!item) return 'neutral';
  const score = item.impact * item.likelihood;
  if (score >= 16) return 'error';
  if (score >= 9) return 'warning';
  if (score >= 5) return 'info';
  return 'success';
};

export const WxbRiskMatrix: React.FC<WxbRiskMatrixProps> = ({
  items,
  likelihoodLabel = '可能性',
  impactLabel = '影响',
  className = '',
  ...props
}) => (
  <div className={joinClasses('wxb-risk-matrix', className)} {...props}>
    <div className="wxb-risk-axis wxb-risk-axis-y">{likelihoodLabel}</div>
    <div className="wxb-risk-grid" aria-label="Risk matrix">
      {SCORE_VALUES.map((likelihood) => (
        <React.Fragment key={likelihood}>
          <div className="wxb-risk-row-label">{likelihood}</div>
          {IMPACT_VALUES.map((impact) => {
            const cellItems = items.filter((item) => item.likelihood === likelihood && item.impact === impact);
            const primary = cellItems[0];
            const tone = resolveTone(primary);

            return (
              <div className={joinClasses('wxb-risk-cell', `wxb-risk-cell-${tone}`)} key={`${likelihood}-${impact}`}>
                {primary && (
                  <>
                    <span className="wxb-risk-cell-label">{primary.label}</span>
                    <span className="wxb-risk-cell-count">{primary.count ?? cellItems.length}</span>
                  </>
                )}
              </div>
            );
          })}
        </React.Fragment>
      ))}
      <div className="wxb-risk-corner" />
      {IMPACT_VALUES.map((impact) => (
        <div className="wxb-risk-col-label" key={impact}>{impact}</div>
      ))}
    </div>
    <div className="wxb-risk-axis wxb-risk-axis-x">{impactLabel}</div>
  </div>
);

